import type { Camera } from "../render/Camera";

/** A body to draw, in world coordinates — engine-agnostic. */
export type RenderBody =
	| { kind: "circle"; x: number; y: number; r: number; angle: number; isStatic: boolean }
	| { kind: "poly"; verts: number[]; isStatic: boolean };

/** A uniform interface over a physics engine running one benchmark scene. */
export interface EngineRunner {
	readonly name: string;
	readonly bodyCount: number;
	/** Advance by exactly `dt` seconds (one fixed step). */
	step: (dt: number) => void;
	/** Visit every body in world coordinates for rendering. */
	forEachBody: (cb: (b: RenderBody) => void) => void;
	/** Release engine-owned world state after an isolated benchmark sample. */
	dispose?: () => void;
}

const TAU = Math.PI * 2;

/** Draw a runner's bodies with the shared Canvas2D camera. Brand-tinted by engine. */
export function drawRunner(
	ctx: CanvasRenderingContext2D,
	cam: Camera,
	runner: EngineRunner,
	hue: number,
): void {
	ctx.clearRect(0, 0, cam.screenW, cam.screenH);

	// Subtle grid.
	ctx.lineWidth = 1;
	ctx.strokeStyle = "rgba(255,255,255,0.04)";
	ctx.beginPath();
	const x0 = Math.floor(cam.screenToWorldX(0));
	const x1 = Math.ceil(cam.screenToWorldX(cam.screenW));
	const y0 = Math.floor(cam.screenToWorldY(0));
	const y1 = Math.ceil(cam.screenToWorldY(cam.screenH));
	for (let x = x0; x <= x1; x++) {
		const sx = cam.worldToScreenX(x);
		ctx.moveTo(sx, 0);
		ctx.lineTo(sx, cam.screenH);
	}
	for (let y = y0; y <= y1; y++) {
		const sy = cam.worldToScreenY(y);
		ctx.moveTo(0, sy);
		ctx.lineTo(cam.screenW, sy);
	}
	ctx.stroke();

	ctx.lineWidth = 1;
	runner.forEachBody((b) => {
		const fill = b.isStatic ? "#2b3145" : `hsl(${hue} 70% 58%)`;
		const stroke = b.isStatic ? "#3c4461" : "rgba(0,0,0,0.45)";
		ctx.fillStyle = fill;
		ctx.strokeStyle = stroke;

		if (b.kind === "circle") {
			const cx = cam.worldToScreenX(b.x);
			const cy = cam.worldToScreenY(b.y);
			const r = b.r * cam.scale;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, TAU);
			ctx.fill();
			ctx.stroke();
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(cx + Math.cos(b.angle) * r, cy + Math.sin(b.angle) * r);
			ctx.strokeStyle = "rgba(0,0,0,0.3)";
			ctx.stroke();
		} else {
			const v = b.verts;
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(v[0]!), cam.worldToScreenY(v[1]!));
			for (let i = 2; i < v.length; i += 2) {
				ctx.lineTo(cam.worldToScreenX(v[i]!), cam.worldToScreenY(v[i + 1]!));
			}
			ctx.closePath();
			ctx.fill();
			ctx.stroke();
		}
	});
}
